import React, { Component } from 'react';
import { connect } from 'react-redux';
import { track } from '../analytics';
import { addLine } from '../redux/actions/bagActions';
import { fetchProducts } from './storefront';
import {
	MERCH_CONTENT,
	EXTERNAL_LINKS,
	FLAGSHIP,
	AUDIENCE,
	contentFor,
	genreLabel,
	genreDot,
	isFlagshipFormat,
} from './catalogContent';
import './Catalog.css';

function money(amount) {
	const n = Number(amount);
	return isNaN(n) ? '' : '$' + n.toFixed(2);
}

class Catalog extends Component {
	constructor(props) {
		super(props);
		this.state = { products: [], loading: true, error: false, filter: 'all', merchSel: {}, modalHandle: null };
		this.closeBook = this.closeBook.bind(this);
		this.handleKey = this.handleKey.bind(this);
	}

	componentDidMount() {
		// Deep-link support: /catalog?book=<handle> (e.g. from a home-page pull-
		// quote) opens that book's detail lightbox once products have loaded.
		const search = (this.props.location && this.props.location.search) || window.location.search;
		const requested = new URLSearchParams(search).get('book');
		fetchProducts()
			.then((products) => {
				const found = requested && products.some((p) => p.handle === requested);
				this.setState({
					products,
					merchSel: this.initSelections(products),
					loading: false,
					modalHandle: found ? requested : null,
				});
			})
			.catch(() => this.setState({ loading: false, error: true }));
		document.addEventListener('keydown', this.handleKey);
	}

	componentWillUnmount() {
		document.removeEventListener('keydown', this.handleKey);
	}

	handleKey(e) {
		if (e.key === 'Escape' && this.state.modalHandle) this.closeBook();
	}

	openBook(handle) {
		this.setState({ modalHandle: handle });
	}

	closeBook() {
		this.setState({ modalHandle: null });
	}

	// Seed each optioned product's selection to its first available variant.
	initSelections(products) {
		const sel = {};
		products.forEach((p) => {
			if (p.options && p.options.length) {
				const v = p.variants.find((vv) => vv.available) || p.variants[0];
				const map = {};
				(v.options || []).forEach((o) => {
					map[o.name] = o.value;
				});
				sel[p.id] = map;
			}
		});
		return sel;
	}

	productByHandle(handle) {
		return this.state.products.find((p) => p.handle === handle);
	}

	// The variant currently chosen for a product (default first variant for
	// single-variant books; the selected size/color for optioned merch).
	selectedVariant(product) {
		if (!product || !product.variants || !product.variants.length) return null;
		const sel = this.state.merchSel[product.id];
		if (!sel) return product.variants[0];
		return (
			product.variants.find((v) => (v.options || []).every((o) => sel[o.name] === o.value)) ||
			product.variants[0]
		);
	}

	setMerchOption(productId, name, value) {
		this.setState((s) => ({
			merchSel: { ...s.merchSel, [productId]: { ...s.merchSel[productId], [name]: value } },
		}));
	}

	// Is there an available variant for `value` given the product's other current
	// selections? Used to grey out sold-out sizes.
	isValueAvailable(product, name, value) {
		const sel = { ...(this.state.merchSel[product.id] || {}), [name]: value };
		const v = product.variants.find((vv) => (vv.options || []).every((o) => sel[o.name] === o.value));
		return v ? v.available : false;
	}

	// Add the selected variant to the Shopify cart (the drawer opens). All cart
	// state — line items, prices, subtotal — comes back from Shopify.
	buy(product) {
		const variant = this.selectedVariant(product);
		if (!variant || !variant.id) return;
		track('add_to_cart', {
			item_name: product.title,
			price: variant.price,
			currency: variant.currency || 'USD',
		});
		this.props.addLine(variant.id);
	}

	renderHero() {
		const primary = this.productByHandle(FLAGSHIP.primaryHandle);
		if (!primary) return null;
		const content = contentFor(FLAGSHIP.primaryHandle) || {};

		return (
			<section className="nf-hero">
				<div className="nf-wrap nf-hero-grid">
					<div
						className="nf-hero-cover nf-clickable"
						role="button"
						tabIndex={0}
						aria-label={'View details for ' + FLAGSHIP.title}
						onClick={() => this.openBook(FLAGSHIP.primaryHandle)}
						onKeyDown={(e) => this.coverKey(e, FLAGSHIP.primaryHandle)}
					>
						{primary.image && <img src={primary.image} alt={FLAGSHIP.title + ' — cover'} />}
					</div>
					<div className="nf-hero-body">
						<p className="nf-eyebrow">{FLAGSHIP.eyebrow}</p>
						<h1>{FLAGSHIP.title}</h1>
						<p className="nf-byline">
							by <em>{content.author}</em> &middot; {genreLabel(content.genre)}
						</p>
						{content.hook && <p className="nf-hero-hook">{content.hook}</p>}
						<div className="nf-formats">{this.formatChips((p) => this.buy(p))}</div>
						<div className="nf-cta-row">
							<button type="button" className="nf-btn" onClick={() => this.buy(primary)}>
								Add to bag <span className="nf-price">{money(primary.price)}</span>
							</button>
							<button type="button" className="nf-btn-text" onClick={() => this.openBook(FLAGSHIP.primaryHandle)}>
								Full details &rarr;
							</button>
						</div>
					</div>
				</div>
			</section>
		);
	}

	// Enter/Space activates a clickable cover.
	coverKey(e, handle) {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			this.openBook(handle);
		}
	}

	// Format buttons/links for the flagship work. onBuy(product) fires for the
	// in-store formats; external formats (Kindle/Audio) link out.
	formatChips(onBuy) {
		return FLAGSHIP.formatHandles.map((handle) => {
			const ext = EXTERNAL_LINKS[handle];
			if (ext) {
				return (
					<a
						key={handle}
						className="nf-fmt"
						href={ext.url}
						target="_blank"
						rel="noopener noreferrer"
						onClick={() =>
							track('buy_intent', { item_name: 'The Ingénue', link_url: ext.url, method: 'external_link', format: ext.label })
						}
					>
						{ext.label} &#8599;
					</a>
				);
			}
			const product = this.productByHandle(handle);
			if (!product) return null;
			const isPrimary = handle === FLAGSHIP.primaryHandle;
			return (
				<button key={handle} type="button" className={'nf-fmt' + (isPrimary ? ' lead' : '')} onClick={() => onBuy(product)}>
					{isPrimary ? 'Paperback' : 'Hardback'} &middot; {money(product.price)}
				</button>
			);
		});
	}

	renderBooks(books) {
		const grid = books.filter((p) => !isFlagshipFormat(p.handle));

		const present = [];
		grid.forEach((p) => {
			const c = contentFor(p.handle);
			if (c && c.genre && !present.includes(c.genre)) present.push(c.genre);
		});

		const { filter } = this.state;
		const shown = grid.filter((p) => {
			if (filter === 'all') return true;
			const c = contentFor(p.handle);
			return c && c.genre === filter;
		});

		return (
			<section className="nf-section nf-wrap" id="books">
				<div className="nf-section-head">
					<div>
						<p className="nf-eyebrow">The Shelf</p>
						<h2>Books</h2>
					</div>
					<span className="nf-count">
						{shown.length} {shown.length === 1 ? 'title' : 'titles'}
					</span>
				</div>

				<div className="nf-filters" role="group" aria-label="Filter by genre">
					<button
						type="button"
						className="nf-chip"
						aria-pressed={filter === 'all'}
						onClick={() => this.setState({ filter: 'all' })}
					>
						All titles
					</button>
					{present.map((code) => (
						<button
							key={code}
							type="button"
							className="nf-chip"
							aria-pressed={filter === code}
							style={{ '--nf-dot': genreDot(code) }}
							onClick={() => this.setState({ filter: code })}
						>
							<span className="nf-dot" />
							{genreLabel(code)}
						</button>
					))}
				</div>

				<div className="nf-grid">{shown.map((p) => this.renderBookCard(p))}</div>
			</section>
		);
	}

	renderBookCard(product) {
		const content = contentFor(product.handle) || {};
		const fmtline = content.format || 'Paperback';

		return (
			<article className="nf-card" key={product.id}>
				<div
					className="nf-card-cover nf-clickable"
					role="button"
					tabIndex={0}
					aria-label={'View details for ' + product.title}
					onClick={() => this.openBook(product.handle)}
					onKeyDown={(e) => this.coverKey(e, product.handle)}
				>
					{content.badge && <span className="nf-ribbon">{content.badge}</span>}
					{product.image && <img src={product.image} alt={product.title + ' — cover'} />}
					<span className="nf-card-view">View details</span>
				</div>
				<div className="nf-card-body">
					<span className="nf-genre" style={{ '--nf-dot': genreDot(content.genre) }}>
						<span className="nf-dot" />
						{genreLabel(content.genre)}
						{content.audience === 'YA' && <span className="nf-aud">&nbsp;&middot; {AUDIENCE.YA.label}</span>}
					</span>
					<h3 className="nf-clickable-text" onClick={() => this.openBook(product.handle)}>{product.title}</h3>
					{content.author && <span className="nf-author">{content.author}</span>}
					{content.hook && <p className="nf-hook">{content.hook}</p>}
					<div className="nf-card-foot">
						<div className="nf-meta">
							<span className="nf-fmtline">{fmtline}</span>
							<span className="nf-cardprice">{money(product.price)}</span>
						</div>
						<button type="button" className="nf-buy" onClick={() => this.buy(product)}>
							Buy
						</button>
					</div>
				</div>
			</article>
		);
	}

	renderMerch(merch) {
		if (!merch.length) return null;
		return (
			<section className="nf-section nf-merch">
				<div className="nf-wrap">
					<div className="nf-section-head">
						<div>
							<p className="nf-eyebrow">The Merch Table</p>
							<h2>Merch</h2>
						</div>
						<span className="nf-count">Wear the press</span>
					</div>
					<div className="nf-merch-grid">
						{merch.map((p) => {
							const info = MERCH_CONTENT[p.handle] || {};
							const variant = this.selectedVariant(p);
							const sel = this.state.merchSel[p.id] || {};
							const soldOut = variant && !variant.available;
							const img = (variant && variant.image) || p.image;
							return (
								<div className="nf-mcard" key={p.id}>
									<div className="nf-mimg">{img && <img src={img} alt={p.title} />}</div>
									<div className="nf-minfo">
										<span className="nf-mkind">{info.kind || 'Merch'}</span>
										<h4>{p.title}</h4>
										<div className="nf-mprice">{money(variant ? variant.price : p.price)}</div>
										{p.options.map((opt) => (
											<label className="nf-opt" key={opt.name}>
												<span className="nf-opt-label">{opt.name}</span>
												<select
													className="nf-select"
													value={sel[opt.name] || ''}
													onChange={(e) => this.setMerchOption(p.id, opt.name, e.target.value)}
												>
													{opt.values.map((val) => {
														const avail = this.isValueAvailable(p, opt.name, val);
														return (
															<option key={val} value={val} disabled={!avail}>
																{val}
																{avail ? '' : ' — sold out'}
															</option>
														);
													})}
												</select>
											</label>
										))}
										<button
											type="button"
											className="nf-buy nf-mbuy"
											disabled={soldOut}
											onClick={() => this.buy(p)}
										>
											{soldOut ? 'Sold out' : 'Add to bag'}
										</button>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</section>
		);
	}

	// Detail lightbox for a book (cover, blurb, publication meta, formats/buy).
	renderModal() {
		const handle = this.state.modalHandle;
		if (!handle) return null;
		const product = this.productByHandle(handle);
		const content = contentFor(handle) || {};
		if (!product) return null;
		const isFlag = handle === FLAGSHIP.primaryHandle;
		const meta = content.meta;
		const metaBits = [];
		if (meta) {
			if (meta.format) metaBits.push(meta.format);
			if (meta.pages) metaBits.push(meta.pages + ' pages');
			if (meta.published) metaBits.push('Published ' + meta.published);
		}

		return (
			<div className="nf-modal-overlay" onClick={this.closeBook}>
				<div className="nf-modal" role="dialog" aria-modal="true" aria-label={product.title} onClick={(e) => e.stopPropagation()}>
					<button type="button" className="nf-modal-x" onClick={this.closeBook} aria-label="Close details">&times;</button>
					<div className="nf-modal-cover">
						{product.image && <img src={product.image} alt={product.title + ' — cover'} />}
					</div>
					<div className="nf-modal-body">
						<span className="nf-genre" style={{ '--nf-dot': genreDot(content.genre) }}>
							<span className="nf-dot" />
							{genreLabel(content.genre)}
							{content.audience === 'YA' && <span className="nf-aud">&nbsp;&middot; {AUDIENCE.YA.label}</span>}
						</span>
						<h2 className="nf-modal-title">{product.title}</h2>
						{content.author && <p className="nf-modal-author">by {content.author}</p>}
						{content.blurb && <p className="nf-modal-blurb">{content.blurb}</p>}
						{(metaBits.length > 0 || (meta && (meta.isbn || meta.issn))) && (
							<div className="nf-modal-meta">
								{metaBits.length > 0 && <span>{metaBits.join(' · ')}</span>}
								{meta && meta.isbn && <span>ISBN {meta.isbn}</span>}
								{meta && meta.issn && <span>ISSN {meta.issn}</span>}
							</div>
						)}
						{isFlag ? (
							<div className="nf-formats nf-modal-formats">
								{this.formatChips((p) => {
									this.buy(p);
									this.closeBook();
								})}
							</div>
						) : (
							<div className="nf-modal-buy">
								<span className="nf-modal-price">{money(product.price)}</span>
								<button
									type="button"
									className="nf-btn"
									onClick={() => {
										this.buy(product);
										this.closeBook();
									}}
								>
									Add to bag
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}

	render() {
		const { products, loading, error } = this.state;
		const isBook = (p) => (p.productType || '').toLowerCase().startsWith('book');
		const books = products.filter(isBook);
		const merch = products.filter((p) => !isBook(p));

		return (
			<div className="nf-catalog">
				{loading && <div className="nf-wrap nf-loading">Loading the shelf…</div>}
				{error && <div className="nf-wrap nf-loading">The shelf is briefly unavailable. Please refresh.</div>}
				{!loading && !error && (
					<React.Fragment>
						{this.renderHero()}
						{this.renderBooks(books)}
						{this.renderMerch(merch)}
					</React.Fragment>
				)}
				{this.renderModal()}
			</div>
		);
	}
}

export default connect(null, { addLine })(Catalog);
